import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography
} from '@mui/material';

import useApi from 'hooks/useApi';
import { CONSENT_TYPE_LABELS, ConsentRecordView, LEGAL_DOCUMENT_LABELS, ResourceConsentDetails } from 'types/legal';
import { describeConsent } from './ConsentStatus';
import { errorMessage, formatLegalDateTime, platformLabel, unwrapEnvelope } from './legalUtils';

interface ConsentHistoryDialogProps {
  open: boolean;
  onClose: () => void;
  resourceUid: string;
  personName?: string;
  // Already-fetched details; otherwise the dialog loads them itself.
  details?: ResourceConsentDetails | null;
}

const byNewest = (a: ConsentRecordView, b: ConsentRecordView) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();

const recordHow = (record: ConsentRecordView) => {
  if (record.source === 'ADMIN') return record.recordedBy?.name ? `Recorded by ${record.recordedBy.name} · Admin` : 'Recorded by an admin';
  const platform = platformLabel(record.platform);
  return platform ? `At login via ${platform}` : 'At login';
};

// Read-only audit trail from GET consent/resource-data/:uid. Records are append-only.
const ConsentHistoryDialog: React.FC<ConsentHistoryDialogProps> = ({ open, onClose, resourceUid, personName, details }) => {
  const { getResourceConsent } = useApi();
  const [loaded, setLoaded] = useState<ResourceConsentDetails | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchRef = useRef(getResourceConsent);
  fetchRef.current = getResourceConsent;

  useEffect(() => {
    if (!open || details || !resourceUid) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setLoaded(null);
    fetchRef
      .current(resourceUid)
      .then((response: any) => {
        if (!cancelled) setLoaded(unwrapEnvelope<ResourceConsentDetails>(response));
      })
      .catch((err: any) => {
        if (!cancelled) setError(errorMessage(err, 'Could not load the consent history.'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, resourceUid, details]);

  const data = details ?? loaded;
  const history = [...(data?.history ?? [])].sort(byNewest);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth scroll="paper" aria-labelledby="consent-history-title">
      <DialogTitle id="consent-history-title">
        Consent history
        {personName && (
          <Typography variant="body2" color="text.secondary" component="span" sx={{ display: 'block' }} data-no-translate="true">
            {personName}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent dividers>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress aria-label="Loading consent history" />
          </Box>
        )}
        {!loading && error && <Alert severity="error">{error}</Alert>}
        {!loading && !error && data && (
          <Stack spacing={2}>
            <Stack spacing={1}>
              {(
                [
                  ['PRIVACY_TERMS', data.current?.privacyTerms, data.privacyTermsEnabled],
                  ['SMS', data.current?.sms, data.smsEnabled]
                ] as const
              ).map(([type, state, enabled]) => {
                const described = describeConsent(state, type, enabled);
                return (
                  <Stack key={type} direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Typography variant="subtitle2">{CONSENT_TYPE_LABELS[type]}</Typography>
                    <Chip size="small" label={described.label} color={described.color} variant="outlined" />
                    <Typography variant="body2" color="text.secondary">
                      {described.detail}
                    </Typography>
                  </Stack>
                );
              })}
            </Stack>
            <Divider />
            {history.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Nothing recorded yet.
              </Typography>
            ) : (
              <Stack component="ol" spacing={1.5} sx={{ listStyle: 'none', p: 0, m: 0 }} aria-label="Consent records, newest first">
                {history.map((record) => (
                  <Box component="li" key={record.uid} sx={{ p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1.5 }}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                      <Typography variant="subtitle2">{CONSENT_TYPE_LABELS[record.type] ?? record.type}</Typography>
                      <Chip
                        size="small"
                        label={record.action === 'GRANTED' ? 'Granted' : 'Withdrawn'}
                        color={record.action === 'GRANTED' ? 'success' : 'warning'}
                      />
                      <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto !important' }}>
                        {formatLegalDateTime(record.createdAt)}
                      </Typography>
                    </Stack>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {recordHow(record)}
                    </Typography>
                    {record.documents?.length > 0 && (
                      <Typography variant="body2" color="text.secondary">
                        {record.documents.map((d) => `${LEGAL_DOCUMENT_LABELS[d.type] ?? d.type} v${d.version}`).join(', ')}
                      </Typography>
                    )}
                    {record.phoneNumber && (
                      <Typography variant="body2" color="text.secondary" data-no-translate="true">
                        Number: {record.countryCode ? `+${String(record.countryCode).replace(/^\+/, '')} ` : ''}
                        {record.phoneNumber}
                      </Typography>
                    )}
                    {record.evidenceNote && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontStyle: 'italic' }} data-no-translate="true">
                        “{record.evidenceNote}”
                      </Typography>
                    )}
                  </Box>
                ))}
              </Stack>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} variant="outlined" color="secondary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConsentHistoryDialog;
